/**
 * Telling a failure that will pass from one that never will.
 *
 * This is the most consequential decision the sync layer makes, and it is
 * wrong in both directions:
 *
 *   Calling a permanent failure transient  → the write retries for ever. The
 *     sale is safe, the queue shows it waiting, someone eventually looks.
 *   Calling a transient failure permanent  → the write is quarantined and the
 *     sale needs re-entering by hand. A lost sale.
 *
 * So the rule is: **when in doubt, transient.** A sale stuck in a visible
 * queue is recoverable; a sale thrown away because the wifi hiccuped is not.
 */

export type FailureKind = 'transient' | 'permanent'

export interface Failure {
  kind: FailureKind
  /** Postgres SQLSTATE, PostgREST code, or one of ours ('network', 'HTTP_503'). */
  code: string
  /** Shown to the cashier. Turkish, and says what to do where possible. */
  message: string
}

/**
 * SQLSTATE classes that mean "the server will refuse this for ever".
 *
 * 23xxx, integrity constraint violation. For this app that is nearly always
 *   the orders_guard trigger refusing a state transition (23514), or a NOT
 *   NULL column arriving empty.
 * 42501, insufficient_privilege. An RLS policy said no: the writer is not a
 *   member of the tenant any more, or lacks the role for what they attempted.
 * 22xxx, data exception. A malformed value; the wrong type in a column.
 */
function isPermanentSqlState(code: string): boolean {
  if (code === '42501') return true
  return /^(22|23)/.test(code)
}

/**
 * SQLSTATEs inside those classes that are nevertheless worth retrying.
 *
 * Serialization failures and deadlocks are the database asking to be asked
 * again, not refusing.
 */
const RETRYABLE_SQLSTATES = new Set([
  '40001', // serialization_failure
  '40P01', // deadlock_detected
  '53300', // too_many_connections
  '57014', // query_canceled, statement timeout
])

/** What to tell the cashier. Vague is worse than long here. */
function reasonFor(code: string): string {
  switch (code) {
    case '42501':
      return 'Bu işlem için yetkiniz yok. Hesabınız kaldırılmış olabilir.'
    case '23514':
      return 'Adisyonun durumu sunucuda kabul edilmedi. Adisyon başka bir cihazdan kapatılmış olabilir.'
    case '23505':
      return 'Bu kayıt zaten var.'
    case '23502':
      return 'Kayıtta eksik alan var.'
    case 'PGRST301':
      return 'Oturum süresi doldu. Çıkış yapıp tekrar girin.'
    case 'PGRST204':
      return 'Kayıt sunucudaki tabloya uymadı. Uygulamayı güncelleyin.'
    default:
      return 'Bu kayıt sunucuya gönderilemedi.'
  }
}

/**
 * Classifies whatever came back from a write attempt.
 *
 * Accepts the raw thing rather than a typed error because the failure can
 * arrive three ways: a PostgrestError object, a thrown fetch TypeError, or an
 * HTTP status with no body at all.
 */
export function classify(err: unknown): Failure {
  // Nothing left the device. fetch() rejects with a TypeError when there is no
  // route to the host, which offline is the normal case rather than a fault.
  if (err instanceof TypeError) {
    return { kind: 'transient', code: 'network', message: 'Bağlantı yok' }
  }

  const e = err as { code?: unknown; status?: unknown; message?: unknown } | null
  const code = typeof e?.code === 'string' ? e.code : ''
  const status = typeof e?.status === 'number' ? e.status : undefined

  // An aborted request is a request that never got an answer.
  if ((err as { name?: string })?.name === 'AbortError') {
    return { kind: 'transient', code: 'aborted', message: 'İstek yarıda kaldı' }
  }

  // The database asking to be asked again.
  if (RETRYABLE_SQLSTATES.has(code)) {
    return { kind: 'transient', code, message: 'Sunucu meşgul' }
  }

  if (code && isPermanentSqlState(code)) {
    return { kind: 'permanent', code, message: reasonFor(code) }
  }

  // PostgREST's own codes. PGRST301 is an expired token, which sounds
  // transient and is not: the queue cannot fix it by waiting, only a fresh
  // sign-in can. Surfacing it tells the cashier to do exactly that.
  if (code.startsWith('PGRST')) {
    return { kind: 'permanent', code, message: reasonFor(code) }
  }

  if (status !== undefined) {
    // 5xx is the server having a bad time; 408 and 429 are explicit
    // invitations to retry.
    if (status >= 500 || status === 408 || status === 429) {
      return {
        kind: 'transient',
        code: `HTTP_${status}`,
        message: 'Sunucu şu an cevap vermiyor',
      }
    }
    // 401 without a PostgREST code: the token needs refreshing, which the
    // client does on its own. Worth another attempt before giving up.
    if (status === 401) {
      return { kind: 'transient', code: 'HTTP_401', message: 'Oturum yenileniyor' }
    }
    if (status === 403) {
      return { kind: 'permanent', code: 'HTTP_403', message: reasonFor('42501') }
    }
  }

  // Anything unrecognised is treated as transient on purpose, see the note at
  // the top of this file. An unknown failure that turns out to be permanent
  // costs a queue entry someone has to look at; the reverse costs a sale.
  return {
    kind: 'transient',
    code: code || (status !== undefined ? `HTTP_${status}` : 'unknown'),
    message: 'Bilinmeyen hata',
  }
}
